import React, { useEffect, useState } from 'react';
import { GetRequest, PostRequest } from 'components/common';

// Custom OneDrive file browser using Microsoft Graph API
// This uses our existing OAuth tokens instead of the OneDrive SDK's separate auth flow
const OneDrivePicker = ({ dataSourceId, onWorkbooksPicked, onCancel }) => {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch available Excel files from OneDrive via our backend
  useEffect(() => {
    setLoading(true);
    GetRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/browse-files`,
      callback: (data) => {
        setFiles(data || []);
        setLoading(false);
      },
      401: () => {
        setError('Authentication failed. Please reconnect your Microsoft account.');
        setLoading(false);
      },
      422: (response) => {
        setError(response?.error || 'Failed to load files from OneDrive');
        setLoading(false);
      }
    });
  }, [dataSourceId]);

  const toggleFileSelection = (file) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleAddSelected = () => {
    if (selectedFiles.length === 0) return;

    setSaving(true);
    const workbooks = selectedFiles.map(file => ({
      id: file.id,
      name: file.name,
      url: file.webUrl
    }));

    PostRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/pick-workbooks`,
      data: { workbooks },
      callback: (result) => {
        setSaving(false);
        onWorkbooksPicked(result.all_workbooks || []);
      },
      422: (result) => {
        setSaving(false);
        setError(`Failed to add workbooks: ${result?.error || 'Unknown error'}`);
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="onedrive-file-browser" style={{
      border: '1px solid #ddd',
      borderRadius: '4px',
      padding: '15px',
      marginTop: '10px',
      backgroundColor: '#f9f9f9'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <strong>
          <i className="fa fa-cloud" style={{ marginRight: '5px' }}></i>
          Select Excel Files from OneDrive
        </strong>
        <button
          type="button"
          className="btn btn-link btn-sm"
          onClick={onCancel}
          style={{ padding: '0' }}
        >
          <i className="fa fa-times"></i>
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <i className="fa fa-spinner fa-spin"></i> Loading files from OneDrive...
        </div>
      )}

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '10px' }}>
          <i className="fa fa-exclamation-circle"></i> {error}
        </div>
      )}

      {!loading && !error && files.length === 0 && (
        <div className="alert alert-info">
          No Excel files found in your OneDrive. Upload some .xlsx files to OneDrive first.
        </div>
      )}

      {!loading && !error && files.length > 0 && (
        <>
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #eee',
            borderRadius: '4px',
            backgroundColor: '#fff'
          }}>
            {files.map(file => {
              const isSelected = selectedFiles.some(f => f.id === file.id);
              return (
                <div
                  key={file.id}
                  onClick={() => toggleFileSelection(file)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 12px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    style={{ marginRight: '10px' }}
                  />
                  <i className="fa fa-file-excel-o" style={{ color: '#217346', marginRight: '10px', fontSize: '18px' }}></i>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: isSelected ? '600' : '400' }}>{file.name}</div>
                    <small style={{ color: '#888' }}>
                      {formatFileSize(file.size)}
                      {file.lastModifiedDateTime && ` • Modified ${formatDate(file.lastModifiedDateTime)}`}
                    </small>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#666' }}>
              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
            </span>
            <div>
              <button
                type="button"
                className="btn btn-default btn-sm"
                onClick={onCancel}
                style={{ marginRight: '10px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddSelected}
                disabled={selectedFiles.length === 0 || saving}
              >
                {saving ? (
                  <><i className="fa fa-spinner fa-spin"></i> Adding...</>
                ) : (
                  <>Add Selected ({selectedFiles.length})</>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OneDrivePicker;
